import { useState } from 'react';

import type { Project } from '@ai-studio/shared';

import { useAgents } from '../../state/agents.js';
import { usePlay } from '../../state/play.js';
import { useWorkspace } from '../../state/workspace.js';
import { Board } from '../Board.js';
import { Chat } from '../Chat.js';
import { PlayTab } from '../PlayTab.js';
import { Icon, type IconName } from '../ui/icons.js';

/**
 * A project on a phone: what you need to make a game with Prisma, one thumb
 * at a time. Prisma (her chat: describe, she builds), Play (the game, full
 * screen, with touch) and Plan (the game plan board). The desktop editor's
 * panels stay on bigger screens.
 */

type Tab = 'prisma' | 'play' | 'plan';

const TABS: ReadonlyArray<{ id: Tab; label: string; icon: IconName }> = [
  { id: 'prisma', label: 'Prisma', icon: 'sparkles' },
  { id: 'play', label: 'Play', icon: 'play' },
  { id: 'plan', label: 'Plan', icon: 'board' },
];

export function MobileStudio({ project }: { project: Project }) {
  const [tab, setTab] = useState<Tab>('prisma');
  const playing = usePlay((s) => s.state !== 'stopped');
  const running = useAgents((s) => Object.values(s.runtime).some((r) => r.state === 'running'));
  const setView = useWorkspace((s) => s.setView);

  const choose = (next: Tab) => {
    setTab(next);
    // The Play view only renders while it is the open view.
    if (next === 'play') setView('play');
  };

  return (
    <div className="mstudio">
      <header className="mstudio-head">
        <strong className="mstudio-title">{project.name}</strong>
        {running && <span className="mstudio-busy">Prisma is working…</span>}
      </header>
      <main className="mstudio-body">
        <section className="mstudio-pane" hidden={tab !== 'prisma'}>
          <Chat />
        </section>
        <section className="mstudio-pane mstudio-play" hidden={tab !== 'play'}>
          {tab === 'play' && <PlayTab />}
        </section>
        <section className="mstudio-pane" hidden={tab !== 'plan'}>
          <Board />
        </section>
      </main>
      <nav className="mtabs" aria-label="Project">
        {TABS.map((t) => (
          <button key={t.id} type="button" className={`mtab${tab === t.id ? ' is-on' : ''}`} onClick={() => choose(t.id)} aria-current={tab === t.id}>
            <Icon name={t.icon} size={20} />
            <span>{t.label}</span>
            {t.id === 'play' && playing && <span className="mtab-dot" aria-hidden="true" />}
          </button>
        ))}
      </nav>
    </div>
  );
}
